import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useState,
} from 'react';

type Tone = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  tone = 'secondary',
  size = 'md',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; size?: 'sm' | 'md' }) {
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
        size === 'sm' ? 'h-7 px-2 text-[13px]' : 'h-9 px-3',
        tone === 'primary' && 'bg-navy text-white shadow-sm hover:bg-navy-hover',
        tone === 'secondary' && 'border border-navy/25 bg-paper text-navy hover:bg-brand-soft',
        tone === 'ghost' && 'text-navy hover:bg-canvas',
        tone === 'danger' && 'text-flag hover:bg-red-50',
        className,
      )}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        'h-8 w-full rounded-lg border border-line bg-paper px-2.5 outline-none transition-shadow focus:border-brand focus:ring-3 focus:ring-brand/15',
        className,
      )}
    />
  );
}

/** A password box with a button to see what was typed. */
export function PasswordInput({
  className,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={shown ? 'text' : 'password'} className={clsx('pr-10', className)} />
      <button
        type="button"
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 grid w-10 place-items-center text-slate hover:text-ink"
      >
        {shown ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={clsx(
        'h-8 w-full rounded-lg border border-line bg-paper px-1.5 outline-none focus:border-brand focus:ring-3 focus:ring-brand/15',
        className,
      )}
    >
      {children}
    </select>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  /** What is wrong with the value. Shown in red under the field, which also gets a red edge. */
  error?: string | null | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed in as children, which labels it implicitly
    <label
      className={clsx(
        'grid min-w-0 content-start gap-1',
        error && '[&_input]:border-flag [&_select]:border-flag [&_textarea]:border-flag',
        className,
      )}
    >
      <span className="text-[12px] font-medium text-slate">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-[12px] font-medium text-flag">
          {error}
        </span>
      ) : hint ? (
        <span className="text-[12px] text-slate">{hint}</span>
      ) : null}
    </label>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'flag' | 'ok' | 'warn';
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-px text-[11px] font-medium',
        tone === 'neutral' && 'bg-canvas text-slate ring-1 ring-line',
        tone === 'brand' && 'bg-brand-soft text-brand',
        tone === 'flag' && 'bg-red-50 text-flag',
        tone === 'ok' && 'bg-green-50 text-ok',
        tone === 'warn' && 'bg-amber-50 text-warn',
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto mt-16 max-w-md rounded-lg border border-dashed border-line bg-paper p-8 text-center">
      <h2 className="text-base font-semibold">{title}</h2>
      <div className="mt-2 text-slate">{children}</div>
    </div>
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={clsx(
        'w-full rounded-lg border border-line bg-paper px-2.5 py-1.5 outline-none focus:border-brand focus:ring-3 focus:ring-brand/15',
        className,
      )}
    />
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      className="flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
      aria-label={label}
    >
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={clsx(
            '-mb-px shrink-0 border-b-2 px-3 pb-2 text-[13px] font-medium whitespace-nowrap',
            value === t.value
              ? 'border-brand text-brand'
              : 'border-transparent text-slate hover:text-ink',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Tabs as buttons: the open one has a blue background. */
export function PillTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="flex gap-1.5" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={clsx(
            'h-9 rounded-lg px-4 text-[13px] font-medium transition-colors',
            value === t.value
              ? 'bg-navy text-white shadow-sm'
              : 'border border-line bg-paper text-navy hover:bg-canvas',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
